import * as React from "react";
import {
  Modal,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { cn } from "@/shared/lib/utils";
import { Pill } from "@/shared/components/ui/pill";

export type SortOption = "date" | "popular" | "distance";

type SortSelectProps = {
  value: SortOption;
  onValueChange: (value: SortOption) => void;
};

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "date", label: "Por data" },
  { value: "popular", label: "Mais populares" },
  { value: "distance", label: "Perto de mim" },
];

export function SortSelect({ value, onValueChange }: SortSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [dropdownPos, setDropdownPos] = React.useState({ top: 0, right: 0 });
  const triggerRef = React.useRef<View>(null);
  const brand = useCSSVariable("--color-brand") as string;
  const { width } = useWindowDimensions();

  const selectedLabel = SORT_OPTIONS.find((opt) => opt.value === value)?.label;

  const handleOpen = () => {
    triggerRef.current?.measure(
      (_x, _y, triggerWidth, triggerHeight, pageX, pageY) => {
        setDropdownPos({
          top: pageY + triggerHeight + 6,
          right: width - pageX - triggerWidth,
        });
        setOpen(true);
      }
    );
  };

  const handleSelect = (selectedValue: SortOption) => {
    onValueChange(selectedValue);
    setOpen(false);
  };

  return (
    <>
      {/* Pill Trigger Button — View wrapper needed for measure() */}
      <Pressable
        ref={triggerRef}
        className="flex-row items-center justify-center gap-1.5 px-4 py-2"
        onPress={handleOpen}
        accessibilityRole="button"
        accessibilityLabel={`Sort by: ${selectedLabel}`}
        accessibilityHint="Double tap to change sort order"
      >
        <Text className="text-foreground text-sm font-medium">
          {selectedLabel}
        </Text>
        <Ionicons name="swap-vertical-outline" size={16} color={"white"} />
      </Pressable>

      {/* Dropdown card */}
      <Modal
        visible={open}
        transparent
        animationType="none"
        onRequestClose={() => setOpen(false)}
      >
        <Pressable className="flex-1" onPress={() => setOpen(false)}>
          <View
            className="bg-popover border-border absolute rounded-xl border shadow-lg"
            style={{
              top: dropdownPos.top,
              right: dropdownPos.right,
              minWidth: 160,
            }}
            onStartShouldSetResponder={() => true}
          >
            {SORT_OPTIONS.map((option) => {
              const isSelected = option.value === value;
              return (
                <Pressable
                  key={option.value}
                  className={cn(
                    "flex-row items-center justify-between px-4 py-3",
                    isSelected && "bg-secondary",
                    option.value === SORT_OPTIONS[0].value && "rounded-t-xl",
                    option.value ===
                      SORT_OPTIONS[SORT_OPTIONS.length - 1].value &&
                      "rounded-b-xl"
                  )}
                  onPress={() => handleSelect(option.value)}
                >
                  <Text
                    className={cn(
                      "text-foreground text-sm",
                      isSelected && "text-brand font-semibold"
                    )}
                  >
                    {option.label}
                  </Text>
                  {isSelected && (
                    <Ionicons
                      name="checkmark"
                      size={16}
                      color={brand}
                      style={{ marginLeft: 12 }}
                    />
                  )}
                </Pressable>
              );
            })}
          </View>
        </Pressable>
      </Modal>
    </>
  );
}
